import {BalloonEventModel, Language} from 'ts-core';
import React from 'react';
import {useHref} from 'react-router-dom';
import {FiCalendar} from "react-icons/fi";
import {useTranslations} from '../../contexts/Translations';
import {trackMixpanel} from '../../utils/mixpanel';

export interface AddCalendarButtonProps {
    event: BalloonEventModel;
    trackingSource?: string;
}

/**
 * Drop trailing empty HTML blocks (stray <br>, empty <p>/<ul>/<ol>/<li>/<div>)
 * and whitespace from a description so the appended event link sits directly
 * under the text instead of after a large blank gap that Google Calendar
 * renders for those empty blocks. Only strips blocks whose content is empty,
 * so paragraphs with real text keep their closing tags.
 */
function stripTrailingEmptyHtml(html: string): string {
    let out = html;
    let prev: string;
    do {
        prev = out;
        out = out
            .replace(/(?:\s|&nbsp;)+$/gi, '')
            .replace(/<br\s*\/?>$/i, '')
            .replace(/<(p|ul|ol|li|div)>(?:\s|&nbsp;|<br\s*\/?>)*<\/\1>$/i, '');
    } while (out !== prev);

    return out;
}

export function AddCalendarButton({event, trackingSource}: AddCalendarButtonProps) {
    const {translated} = useTranslations();

    // Link back to this event on the CURRENT site so the saved calendar entry
    // points to where the user actually found it. useHref is router-aware:
    //  - embeds (HashRouter)  -> host page + '#/event/<slug>'
    //  - main site (Browser)  -> '<origin>/event/<slug>' (incl. basename)
    const eventHref = useHref(`/event/${event.slug ?? event.id}`);
    const eventUrl = React.useMemo(() => {
        if (typeof window === 'undefined') {
            return '';
        }
        const {origin, pathname, search} = window.location;
        const hashIndex = eventHref.indexOf('#');
        // Hash routing keeps the route in the fragment of the current host page,
        // so preserve that page's path + query and append the '#/event/...' part.
        if (hashIndex !== -1) {
            return `${origin}${pathname}${search}${eventHref.slice(hashIndex)}`;
        }
        return `${origin}${eventHref}`;
    }, [eventHref]);

    const formatDateForGoogle = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const details = [stripTrailingEmptyHtml(event.description?.getText(Language.de) ?? ''), eventUrl]
        .filter(Boolean)
        .join('\n\n');

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
        '🎈' + event.title
    )}&dates=${formatDateForGoogle(event.start)}/${formatDateForGoogle(
        event.end || event.start
    )}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(event.locationName)}`;

    const onClick = () => {
        trackMixpanel('EventDetailPage', 'CalendarAdd:Click', {
            eventId: event.id,
            eventName: event.title,
            source: trackingSource,
        });
        window.open(googleCalendarUrl, '_blank');
    };

    return (
        <button
            onClick={onClick}
            className={"flex justify-center items-center w-[36px] h-[36px] text-balloon hover:text-white bg-transparent hover:bg-balloon rounded-full p-2"}
            aria-label={translated("add_to_calendar")}
            title={translated("add_to_calendar")}
        ><FiCalendar size={20}/></button>
    );
}
